import { useCallback, useEffect, useState } from "react";
import { BellRing, X } from "lucide-react";
import api from "../services/api";
import { suppressAlert } from "./Alerts";

const REFRESH_MS = 5 * 60 * 1000;

/**
 * Scrolling bar of the active alerts at the top of every page (pauses on hover). Click it to open the Alerts page; the × on an
 * alert suppresses it for good. Hidden when there is nothing to show.
 */
const AlertsMarquee = ({ onOpen, refreshKey }) => {
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(async () => {
    try {
      const data = await api.get("/api/v1/alerts");
      setAlerts(Array.isArray(data) ? data : []);
    } catch (error) {
      // the bar is a convenience; a failure (e.g. a role without access) just hides it
      setAlerts([]);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load, refreshKey]);

  if (!alerts.length) return null;

  const suppress = async (e, alert) => {
    e.stopPropagation();
    try {
      await suppressAlert(alert.id);
    } finally {
      load();
    }
  };

  const duration = Math.max(20, alerts.reduce((n, a) => n + a.title.length + a.message.length, 0) / 12);

  return (
    <div
      role="status"
      onClick={onOpen}
      title="Open Alerts"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: "#fff7ed",
        border: "1px solid #fdba74",
        color: "#9a3412",
        borderRadius: 8,
        padding: "6px 10px",
        margin: "0 0 12px",
        cursor: "pointer",
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes tt-alert-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .tt-alert-track { display: inline-flex; gap: 48px; white-space: nowrap; animation: tt-alert-marquee ${duration}s linear infinite; }
        .tt-alert-window:hover .tt-alert-track { animation-play-state: paused; }
      `}</style>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700, flexShrink: 0 }}>
        <BellRing size={15} /> Alerts ({alerts.length})
      </span>
      <div className="tt-alert-window" style={{ overflow: "hidden", flex: 1 }}>
        <div className="tt-alert-track">
          {[...alerts, ...alerts].map((alert, i) => (
            <span key={`${alert.id}-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <strong>{alert.title}:</strong> {alert.message.length > 180 ? `${alert.message.slice(0, 180)}…` : alert.message}
              <button
                type="button"
                onClick={(e) => suppress(e, alert)}
                title="Suppress this alert (it will not show again)"
                style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0, display: "inline-flex" }}
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AlertsMarquee;
